import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { stripHtml } from '../helpers';
import { ImpactBadge, DataTable, DiffText, WhyThisMatters } from '../components/Shared.jsx';

function SeoScore({ before, after }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ fontSize: 11, color: T.textSecondary }}>SEO</Typography>
      <Typography sx={{ fontSize: 13, color: T.error, fontWeight: 600 }}>{before}</Typography>
      <Typography sx={{ fontSize: 11, color: T.textSecondary }}>→</Typography>
      <Typography sx={{ fontSize: 13, color: T.success, fontWeight: 600 }}>{after}</Typography>
    </Box>
  );
}

export function EpisodeOptimizer({ data }) {
  const { optimizations = {}, tips = [], keywordPlacement = [], summary } = data;
  const { title = {}, description = {} } = optimizations;

  return (
    <Box>
      {['title', 'description'].map((field) => {
        const f = field === 'title' ? title : description;
        if (!f.original && !f.optimized) return null;
        return (
          <Box key={field} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>{field}</Typography>
              {f.seoScore && <SeoScore before={f.seoScore.before} after={f.seoScore.after} />}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Box sx={{ p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', mb: 1 }}>Before</Typography>
                <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: T.textSecondary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{stripHtml(f.original)}</Typography>
              </Box>
              <Box sx={{ p: 2, background: 'rgba(34,197,94,0.05)', border: `1px solid rgba(34,197,94,0.3)`, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.success, textTransform: 'uppercase', mb: 1 }}>After</Typography>
                <DiffText before={stripHtml(f.original)} after={stripHtml(f.optimized)} sx={{ color: T.text }} />
              </Box>
            </Box>
            {f.reasoning && <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 1, fontStyle: 'italic' }}>{f.reasoning}</Typography>}
          </Box>
        );
      })}

      {keywordPlacement.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Keyword Placement</Typography>
          <DataTable
            columns={['Keyword', 'Field', 'Position']}
            rows={keywordPlacement.map((kp) => [kp.keyword, kp.placement, kp.position])}
          />
        </Box>
      )}

      {tips.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Tips</Typography>
          {tips.map((t, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1, display: 'flex', gap: 1 }}>
              <ImpactBadge value={t.impact} />
              <Box sx={{ minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <Typography sx={{ fontSize: 13, color: T.text, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{t.tip}</Typography>
                {t.example && <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.25, fontStyle: 'italic', wordBreak: 'break-word', overflowWrap: 'break-word' }}>e.g. {t.example}</Typography>}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {summary && (
        <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, color: T.textSecondary }}>{summary}</Typography>
        </Box>
      )}

      <WhyThisMatters text="Better titles and descriptions improve search visibility and click-through rates." citations={data.expertCitations} />
    </Box>
  );
}
